import { useEffect, useState } from "react";
import { api, formatErr } from "../../lib/api";
import { toast } from "sonner";
import ResourceAdmin from "./ResourceAdmin";

export function AdminTicketTiers() {
    return (
        <ResourceAdmin
            title="Ticket Tiers"
            resource="ticket-tiers"
            listKey="name"
            emptyValues={{ is_active: true, is_season_pass: false, sort_order: 0, price_usd: 25 }}
            fields={[
                { key: "name", label: "Name (e.g. General Admission)" },
                { key: "description", label: "Description", type: "textarea" },
                { key: "price_usd", label: "Price (USD)", type: "number" },
                { key: "is_season_pass", label: "Season Pass?", type: "boolean", help: "Mark as a season-long pass" },
                { key: "sort_order", label: "Sort Order", type: "number" },
                { key: "is_active", label: "Active", type: "boolean", help: "Visible on site" },
            ]}
        />
    );
}

export function AdminTicketOrders() {
    const [items, setItems] = useState([]);
    const load = () => api.get("/tickets/orders").then((r) => setItems(r.data));
    useEffect(() => { load(); }, []);
    const revenue = items.filter((i) => i.payment_status === "paid").reduce((s, i) => s + (i.total_amount || 0), 0);

    const resend = async (id) => {
        try {
            await api.post(`/tickets/orders/${id}/resend-receipt`);
            toast.success("Receipt resent");
        } catch (err) { toast.error(formatErr(err)); }
    };

    const toggleRevoke = async (o) => {
        try {
            if (o.revoked) {
                await api.put(`/tickets/orders/${o.id}/unrevoke`);
                toast.success("Ticket re-activated");
            } else {
                const reason = window.prompt("Revoke reason?", "Revoked by admin");
                if (reason === null) return;
                await api.put(`/tickets/orders/${o.id}/revoke`, { reason });
                toast.warning("Ticket revoked");
            }
            load();
        } catch (err) { toast.error(formatErr(err)); }
    };

    return (
        <div data-testid="admin-ticket-orders">
            <div className="text-gold text-[10px] tracking-[0.4em] uppercase font-bold mb-2">Inbox</div>
            <h1 className="h-display text-4xl mb-2">Ticket Orders</h1>
            <p className="text-ink text-sm mb-6">{items.length} orders · <span className="text-gold">${revenue.toFixed(2)}</span> paid revenue</p>
            <div className="card-flat overflow-x-auto">
                <table className="w-full text-sm">
                    <thead className="bg-bg-soft border-b border-line">
                        <tr>
                            <th className="text-left p-3 text-[10px] uppercase tracking-widest text-ink">Buyer</th>
                            <th className="text-left p-3 text-[10px] uppercase tracking-widest text-ink">Tier</th>
                            <th className="text-left p-3 text-[10px] uppercase tracking-widest text-ink">Qty</th>
                            <th className="text-left p-3 text-[10px] uppercase tracking-widest text-ink">Total</th>
                            <th className="text-left p-3 text-[10px] uppercase tracking-widest text-ink">Status</th>
                            <th className="text-left p-3 text-[10px] uppercase tracking-widest text-ink">Date</th>
                            <th className="text-right p-3 text-[10px] uppercase tracking-widest text-ink">Action</th>
                        </tr>
                    </thead>
                    <tbody>
                        {items.map((o) => (
                            <tr key={o.id} className="border-t border-line" data-testid={`order-${o.id}`}>
                                <td className="p-3">
                                    <div className="text-white">{o.buyer_name}</div>
                                    <div className="text-ink text-xs">{o.buyer_email}</div>
                                </td>
                                <td className="p-3">{o.tier_name} {o.is_season_pass && <span className="text-gold text-xs ml-1">· Season</span>}</td>
                                <td className="p-3 text-ink">{o.quantity}</td>
                                <td className="p-3 h-display text-gold">${o.total_amount.toFixed(2)}</td>
                                <td className="p-3">
                                    <span className={`text-xs px-2 py-1 ${o.revoked ? "bg-red-900 text-red-300" : o.payment_status === "paid" ? "bg-green-900 text-green-300" : "bg-bg-soft text-ink"}`}>
                                        {o.revoked ? "revoked" : o.payment_status}
                                    </span>
                                </td>
                                <td className="p-3 text-ink text-xs">{new Date(o.created_at).toLocaleString()}</td>
                                <td className="p-3 text-right">
                                    {o.payment_status === "paid" && (
                                        <div className="inline-flex gap-3">
                                            <button
                                                onClick={() => resend(o.id)}
                                                data-testid={`order-resend-${o.id}`}
                                                className="text-gold text-[10px] uppercase tracking-widest hover:text-gold-bright"
                                            >
                                                Resend
                                            </button>
                                            <button
                                                onClick={() => toggleRevoke(o)}
                                                data-testid={`order-revoke-${o.id}`}
                                                className={`text-[10px] uppercase tracking-widest hover:opacity-80 ${o.revoked ? "text-green-400" : "text-red-400"}`}
                                            >
                                                {o.revoked ? "Restore" : "Revoke"}
                                            </button>
                                        </div>
                                    )}
                                </td>
                            </tr>
                        ))}
                        {items.length === 0 && (
                            <tr><td colSpan={7} className="p-12 text-center text-ink">No orders yet.</td></tr>
                        )}
                    </tbody>
                </table>
            </div>
        </div>
    );
}
